import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { runAiJob } from "@/lib/aiJobs";
import { Button } from "@/components/ui/button";
import { TEST_IDS } from "@/constants/testIds";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Loader2, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import PhotoPicker from "@/components/PhotoPicker";
import SaveButton from "@/components/SaveButton";

const BACKGROUNDS = ["studio", "rooftop", "boutique", "street", "garden"];

export default function TryOn() {
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState([]);
  const [bg, setBg] = useState("studio");
  const [userPhoto, setUserPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const progressTimerRef = useRef(null);

  useEffect(() => {
    api.get("/wardrobe").then(({ data }) => setItems(data.filter((i) => i.status === "available")));
    api.get("/tryon").then(({ data }) => setHistory(data));
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, []);

  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  // Simulated ramp — real Gemini try-ons take 20–40s and give no progress
  // signal. Ramp 0→85% over ~30s using an easing curve, then hold until the
  // response resolves, at which point we snap to 100%.
  const startProgressRamp = () => {
    setProgress(0);
    const start = Date.now();
    const target = 85;
    const durationMs = 30000;
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      // ease-out curve so it feels fast at first, slows near the cap
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 2);
      setProgress(Math.min(target, Math.round(eased * target)));
      if (elapsed >= durationMs) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    }, 200);
  };

  const stopProgressRamp = (finalValue = 100) => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    setProgress(finalValue);
  };

  const run = async ({ regenerate = false } = {}) => {
    if (picked.length === 0) { toast.error("Pick at least one piece"); return; }
    setLoading(true);
    if (!regenerate) setResult(null);
    startProgressRamp();
    try {
      const fd = new FormData();
      fd.append("item_ids", picked.join(","));
      fd.append("background", bg);
      if (userPhoto) fd.append("user_photo", userPhoto);
      if (regenerate) fd.append("regenerate", "true");
      // /tryon returns 202 { job_id } now — runAiJob polls the ai_jobs row
      // until Nano Banana finishes (up to 180s). This bypasses Cloudflare's
      // 100s response cap that was killing sync try-ons in production.
      const data = await runGated("tryons", () =>
        runAiJob(
          () => api.post("/tryon", fd, { headers: { "Content-Type": "multipart/form-data" } }),
        ),
      );
      stopProgressRamp(100);
      setResult(data);
      setHistory((h) => [data, ...h]);
    } catch (e) {
      stopProgressRamp(0);
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw
        : raw?.message
        || e?.message
        || "Try-on failed. Make sure you uploaded body photos in onboarding.";
      toast.error(msg);
    } finally {
      // small delay so users see 100% for a beat
      setTimeout(() => setLoading(false), 350);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Premium · Try-On Studio</p>
          <h1 className="editorial-h1 mt-3">See it on you.</h1>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-6">
          <div className="border border-border p-6 flex items-start gap-4">
            <div className="shrink-0">
              <p className="eyebrow">Step 1 · Photo</p>
              <PhotoPicker
                onSelect={(files) => setUserPhoto(files[0] || null)}
                multiple={false}
                cameraFacing="user"
                testId="tryon-picker"
              >
                <button
                  type="button"
                  className="mt-3 h-24 w-20 bg-muted overflow-hidden border border-border hover:border-foreground transition flex items-center justify-center"
                  aria-label="Add your photo"
                  data-testid="tryon-photo-trigger"
                >
                  {userPhoto
                    ? <img src={URL.createObjectURL(userPhoto)} alt="" className="h-full w-full object-cover" />
                    : <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground text-center px-1">Tap to add</span>}
                </button>
              </PhotoPicker>
            </div>
            <div className="flex-1 text-xs text-muted-foreground leading-relaxed">
              <p className="text-sm text-foreground mb-1">
                {userPhoto ? "Photo ready." : "Using your onboarding body photo by default."}
              </p>
              <p>Tap to take a new photo or upload from gallery — best in even lighting.</p>
            </div>
          </div>

          <div className="border border-border p-6">
            <p className="eyebrow">Step 3 · Setting</p>
            <Select value={bg} onValueChange={setBg}>
              <SelectTrigger className="rounded-none mt-3"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BACKGROUNDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              data-testid={TEST_IDS.tryon.generateBtn}
              onClick={() => run({ regenerate: false })}
              disabled={loading}
              className="mt-6 w-full h-12 rounded-none uppercase tracking-[0.2em] text-xs"
            >
              {loading ? <><Loader2 size={14} className="animate-spin mr-2" />Generating</> : "Generate try-on"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="border border-border p-6">
            <p className="eyebrow">Step 2 · Pick pieces ({picked.length})</p>
            <div className="mt-4 grid grid-cols-3 md:grid-cols-4 gap-2 max-h-72 overflow-y-auto">
              {items.map((it) => {
                const on = picked.includes(it.id);
                return (
                  <button
                    key={it.id}
                    data-testid={TEST_IDS.tryon.itemPick(it.id)}
                    onClick={() => toggle(it.id)}
                    className={`relative border ${on ? "border-foreground" : "border-border"} bg-card transition`}
                  >
                    <div className="aspect-square overflow-hidden bg-muted">
                      <img src={fileUrl(it.image_path)} alt={it.type} className="h-full w-full object-cover" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.15em] p-1.5 truncate">{it.type}</p>
                    {on && <span className="absolute top-1 right-1 bg-accent text-accent-foreground px-1.5 text-[10px]">✓</span>}
                  </button>
                );
              })}
              {items.length === 0 && (
                <p className="col-span-full text-sm text-muted-foreground py-10 text-center">No available items.</p>
              )}
            </div>
          </div>

          <div className="border border-border p-6" data-testid={TEST_IDS.tryon.result}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="eyebrow">Result</p>
              <div className="flex items-center gap-2">
                {result && !loading && (
                  <button
                    type="button"
                    onClick={() => run({ regenerate: true })}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-3 h-8 border border-border hover:border-accent hover:text-accent transition"
                    data-testid="tryon-regenerate-btn"
                    title="Reroll with the same pieces & setting"
                  >
                    <RefreshCw size={11} /> Regenerate
                  </button>
                )}
                {result && (
                  <SaveButton
                    itemType="tryon"
                    itemId={result.id}
                    snapshot={{ result_path: result.result_path, background: result.background }}
                    variant="chip"
                  />
                )}
              </div>
            </div>
            {!result && !loading && (
              <div className="mt-4 aspect-[3/4] bg-muted flex items-center justify-center">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Awaiting generation</p>
              </div>
            )}
            {loading && (
              <div className="mt-4 aspect-[3/4] bg-muted flex flex-col items-center justify-center gap-4 px-8">
                <Loader2 size={28} className="animate-spin text-accent" />
                <div className="w-full max-w-xs">
                  <div className="h-1 w-full bg-foreground/10 overflow-hidden">
                    <div
                      className="h-full bg-accent transition-[width] duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                      data-testid="tryon-progress-bar"
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <span className="pulse-soft">Composing your look</span>
                    <span data-testid="tryon-progress-percent">{progress}%</span>
                  </div>
                </div>
              </div>
            )}
            {result && !loading && (
              <img src={fileUrl(result.result_path)} alt="Try-on" className="mt-4 w-full" />
            )}
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-16">
          <h2 className="editorial-h2">Recent try-ons</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {history.slice(0, 12).map((h) => (
              <div key={h.id} className="border border-border bg-card relative">
                <SaveButton
                  itemType="tryon"
                  itemId={h.id}
                  snapshot={{ result_path: h.result_path, background: h.background }}
                  variant="ghost"
                />
                <div className="aspect-[3/4] bg-muted overflow-hidden">
                  <img src={fileUrl(h.result_path)} alt="" className="h-full w-full object-cover" />
                </div>
                <p className="p-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{h.background}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
